import {
  type DictationOutcome,
  type DictationSessionEndPorts,
  type DictationTriggeredBy,
} from '../../contracts';
import { type ErrorCode } from '../../contracts/dictation-error.types';

// `DictationOutcome` moved to `@heidi/dictate/contracts` — it is
// `universal_dictation_completed`'s `outcome`, i.e. wire vocabulary. Re-exported
// at statement level so every importer and `jest.mock` literal resolves untouched.
export type { DictationOutcome };

const SHORT_DICTATION_THRESHOLD_MS = 2000;

export function isDictationApiError(errorCode: ErrorCode): boolean {
  return (
    errorCode === 'COULD_NOT_CONNECT_TO_HEIDI' || errorCode === 'AUTHENTICATION_REQUIRED'
  );
}

export function errorCodeToOutcome(errorCode: ErrorCode): DictationOutcome {
  switch (errorCode) {
    case 'COULD_NOT_HEAR_LONG':
      return 'could_not_hear_long';
    case 'NO_WORDS_DETECTED':
      return 'no_words_detected';
    case 'PRESS_AND_HOLD':
      return 'press_and_hold';
    case 'NO_INPUT_FIELD_SELECTED':
      return 'no_input_field_selected';
    case 'MIC_PERMISSION_DENIED':
      return 'mic_permission_denied';
    case 'NO_MIC_DEVICES':
      return 'no_mic_devices';
    case 'FAILED_TO_GET_AUDIO_STREAM':
      return 'failed_to_get_audio_stream';
    case 'CHUNK_RECORDER_ERROR':
      return 'chunk_recorder_error';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'unknown';
  }
}

export type DictationSessionEndAnalytics = {
  triggered_by: DictationTriggeredBy;
  hold_duration_ms: number;
  word_count: number;
  dictation_id: string | null;
  hotkey?: string | null;
  output_latency_ms?: number | null;
  target_app?: string;
  underlying_error_code?: string;
};

/**
 * Fire exactly one session-end event, chosen by the error code.
 *
 * The port is the two EMITTERS, not the store: callers pass
 * `dictationSessionEndPorts`, which is the same two emits on the same `track()`
 * path with the same (absent) routing.
 *
 * Both branch rules are the domain's and both are pinned by the suite — an API
 * failure reports `_errored`, everything else `_completed` with an `outcome`; and
 * a sub-2-second `COULD_NOT_HEAR_LONG` / `NO_WORDS_DETECTED` reports NOTHING,
 * because a key-tap that never became speech is not a dictation attempt.
 */
export function trackDictationSessionEnd(
  ports: DictationSessionEndPorts,
  errorCode: ErrorCode,
  ctx: DictationSessionEndAnalytics
): void {
  if (
    ctx.hold_duration_ms < SHORT_DICTATION_THRESHOLD_MS &&
    (errorCode === 'COULD_NOT_HEAR_LONG' || errorCode === 'NO_WORDS_DETECTED')
  ) {
    return;
  }

  if (isDictationApiError(errorCode)) {
    ports.trackErrored({
      triggered_by: ctx.triggered_by,
      hold_duration_ms: ctx.hold_duration_ms,
      word_count: ctx.word_count,
      error_code: errorCode,
      dictation_id: ctx.dictation_id,
      ...(ctx.target_app ? { target_app: ctx.target_app } : {}),
      ...(ctx.underlying_error_code
        ? { underlying_error_code: ctx.underlying_error_code }
        : {}),
    });
    return;
  }

  ports.trackCompleted({
    triggered_by: ctx.triggered_by,
    hold_duration_ms: ctx.hold_duration_ms,
    word_count: ctx.word_count,
    hotkey: ctx.hotkey ?? null,
    output_latency_ms: ctx.output_latency_ms ?? null,
    dictation_id: ctx.dictation_id,
    outcome: errorCodeToOutcome(errorCode),
    ...(ctx.target_app ? { target_app: ctx.target_app } : {}),
  });
}
